import React, { useState, useEffect } from 'react';

const Store = ({ addAppToDesktop }) => {
  console.log('Store component is rendering');
  
  const [selectedNav, setSelectedNav] = useState('home');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [installingApp, setInstallingApp] = useState(null);
  const [installProgress, setInstallProgress] = useState(0);
  const [installedApps, setInstalledApps] = useState({});
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredApps, setFilteredApps] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [customApps, setCustomApps] = useState([]);
  
  // Load installed apps from localStorage
  useEffect(() => {
    try {
      const savedInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      setInstalledApps(savedInstalledApps);
      console.log('Loaded installed apps:', savedInstalledApps);
    } catch (e) {
      console.error('Failed to load installed apps:', e);
    }
    
    // Load custom apps from localStorage
    try {
      const customAppsObj = JSON.parse(localStorage.getItem('webos-custom-apps') || '{}');
      const customAppsList = Object.values(customAppsObj).map(app => ({
        ...app,
        isCustomApp: true
      }));
      setCustomApps(customAppsList);
      console.log('Loaded custom apps:', customAppsList);
    } catch (e) {
      console.error('Failed to load custom apps:', e);
    }
  }, []);
  
  // Listen for custom app publishing events
  useEffect(() => {
    const handleAppPublished = (event) => {
      console.log('App published event detected, refreshing store data', event.detail);
      refreshStoreData();
    };
    
    window.addEventListener('webos-store-app-published', handleAppPublished);
    
    return () => {
      window.removeEventListener('webos-store-app-published', handleAppPublished);
    };
  }, []);
  
  // Refresh store data
  const refreshStoreData = () => {
    try {
      const savedInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      setInstalledApps(savedInstalledApps);
      
      const customAppsObj = JSON.parse(localStorage.getItem('webos-custom-apps') || '{}');
      const customAppsList = Object.values(customAppsObj).map(app => ({
        ...app,
        isCustomApp: true
      }));
      setCustomApps(customAppsList);
    } catch (e) {
      console.error('Failed to refresh apps data:', e);
    }
  };
  
  // Navigation sections
  const navItems = [
    { id: 'home', label: 'Home', icon: 'home' },
    { id: 'apps', label: 'Apps', icon: 'th-large' },
    { id: 'games', label: 'Games', icon: 'gamepad' },
    { id: 'productivity', label: 'Productivity', icon: 'briefcase' },
    { id: 'tools', label: 'Tools', icon: 'tools' },
    { id: 'library', label: 'My Library', icon: 'download' },
  ];
  
  // App data - real apps that can be installed
  const apps = [
    {
      id: 'calculator',
      name: 'Calculator',
      description: 'A powerful calculator with memory functions and clean interface.',
      longDescription: 'Perform basic and advanced calculations with this sleek calculator app. Features include standard arithmetic operations, memory functions, and a responsive interface that fits perfectly with WebOS\'s aesthetic.',
      icon: 'calculator',
      category: 'productivity',
      author: 'WebOS Team',
      rating: 4.5,
      size: '0.8 MB',
      featured: true,
      screenshots: [
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Calculator+Screenshot+1',
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Calculator+Screenshot+2',
      ],
      component: 'Calculator'
    },
    {
      id: 'code-editor',
      name: 'Code Editor',
      description: 'A powerful code editor with syntax highlighting, file management, and multiple themes.',
      longDescription: 'Develop code right in your WebOS environment with this VS Code-like editor. Features include file explorer, syntax highlighting for multiple languages, tabs for multiple open files, and customizable theme options.',
      icon: 'code',
      category: 'productivity',
      author: 'WebOS Team',
      rating: 4.9,
      size: '2.1 MB',
      featured: true,
      screenshots: [
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Code+Editor+Screenshot+1',
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Code+Editor+Screenshot+2',
      ],
      component: 'CodeEditor'
    },
    {
      id: 'tic-tac-toe',
      name: 'Tic Tac Toe',
      description: 'Classic game of Tic Tac Toe with modern UI and multiple difficulty levels.',
      longDescription: 'Enjoy the classic game of Tic Tac Toe with a modern twist. Features include multiple difficulty levels for AI opponent, two-player mode, game history with time travel, and score tracking. Perfect for quick gaming sessions!',
      icon: 'gamepad',
      category: 'games',
      author: 'WebOS Team',
      rating: 4.8,
      size: '1.3 MB',
      featured: true,
      screenshots: [
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Tic+Tac+Toe+Screenshot+1',
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=Tic+Tac+Toe+Game+Board',
      ],
      component: 'TicTacToe'
    },
    {
      id: 'file-manager',
      name: 'File Manager',
      description: 'A powerful file management tool with advanced features.',
      longDescription: 'Organize and manage your files with this powerful file manager. Features include file operations (copy, move, delete), search capabilities, file preview, and easy navigation through your file system.',
      icon: 'folder',
      category: 'tools',
      author: 'WebOS Team',
      rating: 4.7,
      size: '1.5 MB',
      featured: false,
      screenshots: [
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=File+Manager+Screenshot+1',
        'https://via.placeholder.com/800x450/2c2c4a/ffffff?text=File+Manager+Screenshot+2',
      ],
      component: 'FileExplorer'
    },
    // Add more apps here as you implement them
  ];
  
  // Check if an app is installed (more robust check)
  const isAppInstalled = (appId) => {
    // Check our local state first (fastest)
    if (installedApps[appId] === true) {
      return true;
    }
    
    // Double-check localStorage (in case our state is stale)
    try {
      // Check webos-installed-apps
      const savedInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      if (savedInstalledApps[appId] === true) {
        return true;
      }
      
      // Also check desktop icons as a fallback
      const desktopIcons = JSON.parse(localStorage.getItem('webos-desktop-icons') || '[]');
      if (desktopIcons.some(icon => icon.id === appId)) {
        return true;
      }
    } catch (e) {
      console.error('Error checking if app is installed:', e);
    }
    
    return false;
  };
  
  // Get apps with installed status
  const getAppsWithStatus = () => {
    // Add installed status to built-in apps
    const builtInAppsWithStatus = apps.map(app => ({
      ...app,
      installed: isAppInstalled(app.id)
    }));
    
    // Add installed status to custom apps
    const customAppsWithStatus = customApps.map(app => ({
      ...app,
      installed: isAppInstalled(app.id)
    }));
    
    // Combine both types of apps
    return [...builtInAppsWithStatus, ...customAppsWithStatus];
  };
  
  // Launch application
  const launchApp = (app) => {
    // Find app in desktop icons
    const desktopIcons = JSON.parse(localStorage.getItem('webos-desktop-icons') || '[]');
    const appIcon = desktopIcons.find(icon => icon.id === app.id);
    
    if (appIcon) {
      // Create a custom event to request app launch
      const event = new CustomEvent('webos-launch-app', { 
        detail: { appId: app.id }
      });
      window.dispatchEvent(event);
      
      // Show launch animation
      const notification = document.createElement('div');
      notification.style.position = 'fixed';
      notification.style.bottom = '20px';
      notification.style.right = '20px';
      notification.style.backgroundColor = 'rgba(20, 20, 40, 0.8)';
      notification.style.color = 'white';
      notification.style.padding = '15px 20px';
      notification.style.borderRadius = '10px';
      notification.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.3)';
      notification.style.backdropFilter = 'blur(10px)';
      notification.style.border = '1px solid rgba(255, 255, 255, 0.1)';
      notification.style.zIndex = '9999';
      notification.style.transition = 'all 0.3s ease';
      notification.style.transform = 'translateY(100px)';
      notification.style.opacity = '0';
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 15px;">
          <i class="fas fa-${app.icon}" style="color: #4caf50; font-size: 24px;"></i>
          <div>
            <div style="font-weight: 500; margin-bottom: 5px;">Launching ${app.name}...</div>
          </div>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      // Animate notification
      setTimeout(() => {
        notification.style.transform = 'translateY(0)';
        notification.style.opacity = '1';
      }, 100);
      
      // Remove notification after delay
      setTimeout(() => {
        notification.style.transform = 'translateY(100px)';
        notification.style.opacity = '0';
        setTimeout(() => document.body.removeChild(notification), 300);
      }, 2000);
    }
  };
  
  // Featured content for the hero carousel
  const featuredContent = [
    {
      id: 'feature1',
      title: 'Essential WebOS Apps',
      subtitle: 'Discover tools to boost your productivity',
      backgroundImage: 'https://via.placeholder.com/1200x400/2c1e4a/ffffff?text=Essential+Apps',
      color: '#6a3de8',
      appId: 'calculator'
    },
    {
      id: 'feature2',
      title: 'Coming Soon: More Apps',
      subtitle: 'Stay tuned for exciting new applications',
      backgroundImage: 'https://via.placeholder.com/1200x400/1e4a2c/ffffff?text=Coming+Soon',
      color: '#3de86a'
    }
  ];
  
  // App categories
  const categories = [
    { id: 'productivity', title: 'Productivity', icon: 'briefcase' },
    { id: 'tools', title: 'Tools', icon: 'tools' },
    { id: 'games', title: 'Games', icon: 'gamepad' },
    { id: 'utilities', title: 'Utilities', icon: 'cogs' },
    { id: 'entertainment', title: 'Entertainment', icon: 'film' },
    { id: 'education', title: 'Education', icon: 'graduation-cap' },
    { id: 'media', title: 'Media', icon: 'music' }
  ];
  
  // Auto-advance the carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prevSlide => (prevSlide + 1) % featuredContent.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [featuredContent.length]);
  
  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setIsSearchActive(false);
      setFilteredApps([]);
      return;
    }
    
    setIsSearchActive(true);
    const searchResults = getAppsWithStatus().filter(app => 
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      app.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    setFilteredApps(searchResults);
  }, [searchQuery, installedApps]);
  
  // Refresh installed apps list whenever localStorage changes
  useEffect(() => {
    const refreshInstalledApps = () => {
      try {
        const savedInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
        setInstalledApps(savedInstalledApps);
        console.log('Refreshed installed apps:', savedInstalledApps);
      } catch (e) {
        console.error('Failed to refresh installed apps:', e);
      }
    };
    
    // Initial load
    refreshInstalledApps();
    
    // Set up storage event listener to detect changes from other components
    const handleStorageChange = (e) => {
      if (e.key === 'webos-installed-apps') {
        refreshInstalledApps();
      }
    };
    
    // Handle custom refresh event
    const handleCustomRefresh = () => {
      refreshInstalledApps();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('webos-refresh-apps', handleCustomRefresh);
    
    // Force refresh every second to ensure installed apps are up to date
    const interval = setInterval(refreshInstalledApps, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('webos-refresh-apps', handleCustomRefresh);
      clearInterval(interval);
    };
  }, []);
  
  // Installation progress simulation
  useEffect(() => {
    let interval;
    if (installingApp) {
      interval = setInterval(() => {
        setInstallProgress(prev => {
          const newProgress = prev + (Math.random() * 15);
          if (newProgress >= 100) {
            clearInterval(interval);
            // Complete installation after progress reaches 100%
            setTimeout(() => {
              completeInstallation(installingApp);
            }, 500);
            return 100;
          }
          return newProgress;
        });
      }, 300);
    }
    return () => clearInterval(interval);
  }, [installingApp]);
  
  // Complete the installation process
  const completeInstallation = (app) => {
    // Add app to desktop
    if (addAppToDesktop) {
      const desktopApp = {
        id: app.id,
        name: app.name,
        icon: app.icon,
        component: app.component || app.id.charAt(0).toUpperCase() + app.id.slice(1).replace(/-([a-z])/g, g => g[1].toUpperCase())
      };
      console.log('Sending app to desktop:', desktopApp);
      addAppToDesktop(desktopApp);
    } else {
      console.error('addAppToDesktop prop is not available');
      // Try using the global function as fallback
      if (window.addDesktopIcon) {
        const desktopApp = {
          id: app.id,
          name: app.name,
          icon: app.icon,
          component: app.component || app.id.charAt(0).toUpperCase() + app.id.slice(1).replace(/-([a-z])/g, g => g[1].toUpperCase())
        };
        window.addDesktopIcon(desktopApp);
      } else {
        console.error('No mechanism available to add app to desktop');
      }
    }
    
    // Save to localStorage for persistence
    try {
      const currentInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      currentInstalledApps[app.id] = true;
      localStorage.setItem('webos-installed-apps', JSON.stringify(currentInstalledApps));
    
    // Update installed apps state
    setInstalledApps(prev => ({
      ...prev,
      [app.id]: true
    }));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('webos-refresh-apps'));
    } catch (e) {
      console.error('Failed to save installed app:', e);
    }
    
    // Reset installation state
    setInstallingApp(null);
    setInstallProgress(0);
    
    // Show success notification
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.right = '20px';
    notification.style.backgroundColor = 'rgba(20, 20, 40, 0.8)';
    notification.style.color = 'white';
    notification.style.padding = '15px 20px';
    notification.style.borderRadius = '10px';
    notification.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.3)';
    notification.style.backdropFilter = 'blur(10px)';
    notification.style.border = '1px solid rgba(255, 255, 255, 0.1)';
    notification.style.zIndex = '9999';
    notification.style.transition = 'all 0.3s ease';
    notification.style.transform = 'translateY(100px)';
    notification.style.opacity = '0';
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 15px;">
        <i class="fas fa-check-circle" style="color: #4caf50; font-size: 24px;"></i>
        <div>
          <div style="font-weight: 500; margin-bottom: 5px;">${app.name} installed successfully!</div>
          <div style="font-size: 12px; opacity: 0.8;">The app has been added to your desktop</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Animate notification
    setTimeout(() => {
      notification.style.transform = 'translateY(0)';
      notification.style.opacity = '1';
    }, 100);
    
    // Remove notification after delay
    setTimeout(() => {
      notification.style.transform = 'translateY(100px)';
      notification.style.opacity = '0';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 4000);
  };
  
  // Uninstall application
  const uninstallApp = (app) => {
    // Remove app from desktop
    try {
      // 1. Update localStorage desktop icons
      const desktopIcons = JSON.parse(localStorage.getItem('webos-desktop-icons') || '[]');
      const filteredIcons = desktopIcons.filter(icon => icon.id !== app.id);
      localStorage.setItem('webos-desktop-icons', JSON.stringify(filteredIcons));
      
      // 2. Remove from installed apps tracking
      const currentInstalledApps = JSON.parse(localStorage.getItem('webos-installed-apps') || '{}');
      delete currentInstalledApps[app.id];
      localStorage.setItem('webos-installed-apps', JSON.stringify(currentInstalledApps));
      
      // 3. Update installed apps state
      setInstalledApps(prev => {
        const newState = { ...prev };
        delete newState[app.id];
        return newState;
      });
      
      // 4. Reset selected app to update UI
      setSelectedApp({
        ...app,
        installed: false
      });
      
      // 5. Dispatch custom events to notify other components
      window.dispatchEvent(new Event('webos-refresh-apps'));
      
      // 6. Create and dispatch a dedicated uninstall event for Desktop component
      const uninstallEvent = new CustomEvent('webos-uninstall-app', {
        detail: { appId: app.id }
      });
      window.dispatchEvent(uninstallEvent);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.position = 'fixed';
      notification.style.bottom = '20px';
      notification.style.right = '20px';
      notification.style.backgroundColor = 'rgba(20, 20, 40, 0.8)';
      notification.style.color = 'white';
      notification.style.padding = '15px 20px';
      notification.style.borderRadius = '10px';
      notification.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.3)';
      notification.style.backdropFilter = 'blur(10px)';
      notification.style.border = '1px solid rgba(255, 255, 255, 0.1)';
      notification.style.zIndex = '9999';
      notification.style.transition = 'all 0.3s ease';
      notification.style.transform = 'translateY(100px)';
      notification.style.opacity = '0';
      notification.innerHTML = `
        <div style="display: flex; align-items: center; gap: 15px;">
          <i class="fas fa-trash-alt" style="color: #f44336; font-size: 24px;"></i>
          <div>
            <div style="font-weight: 500; margin-bottom: 5px;">${app.name} uninstalled successfully</div>
            <div style="font-size: 12px; opacity: 0.8;">The app has been removed from your desktop</div>
          </div>
        </div>
      `;
      
      document.body.appendChild(notification);
      
      // Animate notification
      setTimeout(() => {
        notification.style.transform = 'translateY(0)';
        notification.style.opacity = '1';
      }, 100);
      
      // Remove notification after delay
      setTimeout(() => {
        notification.style.transform = 'translateY(100px)';
        notification.style.opacity = '0';
        setTimeout(() => document.body.removeChild(notification), 300);
      }, 4000);
      
    } catch (e) {
      console.error('Failed to uninstall app:', e);
    }
  };
  
  // Start installation process
  const installApp = (app) => {
    setInstallingApp(app);
    setInstallProgress(0);
  };
  
  // Listen for app launch events
  useEffect(() => {
    const handleAppLaunch = (e) => {
      const { appId } = e.detail;
      console.log('Request to launch app:', appId);
      // This will be handled by the Desktop component
    };
    
    window.addEventListener('webos-launch-app', handleAppLaunch);
    return () => {
      window.removeEventListener('webos-launch-app', handleAppLaunch);
    };
  }, []);
  
  // Render app card
  const renderAppCard = (app) => {
    const isInstalled = isAppInstalled(app.id);
    
    return (
      <div 
        key={app.id} 
        style={{
          backgroundColor: 'rgba(30, 30, 50, 0.4)',
          backdropFilter: 'blur(10px)',
          borderRadius: '10px',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          border: isInstalled ? '1px solid rgba(70, 130, 220, 0.4)' : '1px solid rgba(255, 255, 255, 0.08)',
          minWidth: '160px',
          position: 'relative'
        }}
        onClick={() => openAppDetails(app)}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)';
          e.currentTarget.style.boxShadow = '0 7px 20px rgba(0, 0, 0, 0.2)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {isInstalled && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(70, 130, 220, 0.9)',
            color: 'white',
            fontSize: '10px',
            padding: '4px 8px',
            borderRadius: '10px',
            fontWeight: '500'
          }}>
            Installed
          </div>
        )}
        <div style={{ 
          backgroundColor: `rgba(75, 90, 125, 0.3)`,
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className={`fas fa-${app.icon}`} style={{ fontSize: '24px' }}></i>
          </div>
        </div>
        <div style={{ padding: '12px' }}>
          <div style={{ fontWeight: '500', marginBottom: '5px' }}>{app.name}</div>
          <div style={{ 
            fontSize: '12px', 
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>{categories.find(c => c.id === app.category)?.title || app.category}</span>
            <span>Free</span>
          </div>
        </div>
      </div>
    );
  };

  // Filter apps by category
  const getAppsByCategory = (categoryId) => {
    return getAppsWithStatus().filter(app => app.category === categoryId);
  };
  
  // App collection component
  const renderAppCollection = (title, appsList, subtitle = null) => (
    <div className="app-collection" style={{ marginBottom: '30px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '15px' 
      }}>
        <div>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            margin: '0 0 4px 0' 
          }}>
            {title}
          </h2>
          {subtitle && (
            <p style={{ 
              fontSize: '13px', 
              color: 'rgba(255, 255, 255, 0.7)', 
              margin: 0 
            }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      
      {appsList.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: `repeat(${Math.min(appsList.length, 5)}, 1fr)`, 
          gap: '15px',
          overflowX: 'auto',
          padding: '4px',
          scrollbarWidth: 'none'
        }}>
          {appsList.map(app => renderAppCard(app))}
        </div>
      ) : (
        <div style={{ 
          backgroundColor: 'rgba(30, 30, 50, 0.4)', 
          borderRadius: '10px',
          padding: '20px',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <i className="fas fa-info-circle" style={{ marginRight: '10px' }}></i>
          No apps available in this category yet. Check back soon!
        </div>
      )}
    </div>
  );
  
  // Open app details
  const openAppDetails = (app) => {
    // Add installed status to the app object
    setSelectedApp({
      ...app,
      installed: isAppInstalled(app.id)
    });
  };
  
  // Close app details and go back
  const closeAppDetails = () => {
    setSelectedApp(null);
  };
  
  // Handle tab switching and navigation
  const handleNavigation = (navId) => {
    setSelectedNav(navId);
    setSelectedCategory(null);
    
    // Clear search when navigating
    if (isSearchActive) {
      setSearchQuery('');
      setIsSearchActive(false);
    }
    
    // Close app details if open
    if (selectedApp) {
      setSelectedApp(null);
    }
  };
  
  // Render search results
  const renderSearchResults = () => {
    if (!isSearchActive) return null;
    
    return (
      <div style={{ padding: '25px' }}>
        <h2 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          marginBottom: '20px' 
        }}>
          Search Results for "{searchQuery}"
        </h2>
        
        {filteredApps.length > 0 ? (
          renderAppCollection('Results', filteredApps)
        ) : (
          <div style={{
            backgroundColor: 'rgba(30, 30, 50, 0.4)',
            borderRadius: '15px',
            padding: '40px 30px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: 'rgba(70, 130, 220, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-search" style={{ fontSize: '32px', opacity: 0.7 }}></i>
            </div>
            <div>
              <h3 style={{ marginBottom: '10px', fontWeight: '500' }}>No results found</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                We couldn't find any apps matching "{searchQuery}". Try a different search term.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render app details view
  const renderAppDetails = () => {
    if (!selectedApp) return null;
    
    // Use our comprehensive check for installation status
    const isCurrentlyInstalled = isAppInstalled(selectedApp.id);
    
    // If the installed status has changed, update the selectedApp
    if (isCurrentlyInstalled !== selectedApp.installed) {
      setSelectedApp({
        ...selectedApp,
        installed: isCurrentlyInstalled
      });
    }
    
    return (
      <div style={{ 
        padding: '0', 
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* App header */}
        <div style={{ 
          padding: '20px 25px',
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          <button
            onClick={closeAppDetails}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              padding: '10px',
              marginRight: '15px',
              cursor: 'pointer',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            margin: 0 
          }}>
            {selectedApp.name}
          </h2>
          
          {isCurrentlyInstalled && (
            <div style={{
              marginLeft: '10px',
              backgroundColor: 'rgba(70, 130, 220, 0.9)',
              color: 'white',
              fontSize: '12px',
              padding: '4px 10px',
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              Installed
            </div>
          )}
        </div>
        
        {/* App content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: '25px'
        }}>
          {/* App hero section */}
          <div style={{ 
            display: 'flex',
            gap: '25px',
            marginBottom: '30px'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              backgroundColor: 'rgba(50, 60, 100, 0.3)',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className={`fas fa-${selectedApp.icon}`} style={{ fontSize: '50px' }}></i>
            </div>
            
            <div style={{ flex: 1 }}>
              <h2 style={{ 
                fontSize: '28px',
                fontWeight: '700',
                marginTop: '0',
                marginBottom: '10px'
              }}>
                {selectedApp.name}
              </h2>
              
              <div style={{ 
                marginBottom: '15px',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '14px'
              }}>
                By {selectedApp.author} • {categories.find(c => c.id === selectedApp.category)?.title || selectedApp.category}
              </div>
              
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <i 
                      key={star}
                      className={`fas fa-star`}
                      style={{ 
                        color: star <= Math.floor(selectedApp.rating) ? '#ffc107' : 'rgba(255, 255, 255, 0.2)',
                        fontSize: '14px'
                      }}
                    ></i>
                  ))}
                  <span style={{ marginLeft: '5px', fontSize: '14px' }}>{selectedApp.rating}</span>
                </div>
                
                <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px' }}>
                  • {selectedApp.size}
                </span>
              </div>
              
              {isCurrentlyInstalled ? (
                <>
                  <div style={{ display: 'flex', gap: '15px' }}>
                <button
                      onClick={(e) => {
                        e.stopPropagation();
                        launchApp(selectedApp);
                      }}
                  style={{
                    backgroundColor: 'rgba(40, 150, 80, 0.8)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 25px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(40, 150, 80, 0.9)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(40, 150, 80, 0.8)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fas fa-play"></i>
                  Open
                </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        uninstallApp(selectedApp);
                      }}
                      style={{
                        backgroundColor: 'rgba(220, 53, 69, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '10px 25px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.9)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.8)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <i className="fas fa-trash-alt"></i>
                      Uninstall
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    installApp(selectedApp);
                  }}
                  disabled={installingApp !== null}
                  style={{
                    backgroundColor: installingApp ? 'rgba(40, 40, 70, 0.5)' : 'rgba(70, 130, 220, 0.8)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    padding: '10px 25px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: installingApp ? 'default' : 'pointer',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseOver={(e) => {
                    if (!installingApp) {
                      e.currentTarget.style.backgroundColor = 'rgba(70, 130, 220, 0.9)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!installingApp) {
                      e.currentTarget.style.backgroundColor = 'rgba(70, 130, 220, 0.8)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {installingApp === selectedApp ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Installing... {Math.round(installProgress)}%
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download"></i>
                      Install
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          
          {/* Install progress bar */}
          {installingApp === selectedApp && (
            <div style={{
              marginBottom: '30px',
              backgroundColor: 'rgba(30, 30, 50, 0.5)',
              borderRadius: '8px',
              overflow: 'hidden',
              height: '6px'
            }}>
              <div 
                style={{
                  backgroundColor: 'rgba(70, 130, 220, 0.8)',
                  height: '100%',
                  width: `${installProgress}%`,
                  transition: 'width 0.3s ease-out'
                }}
              ></div>
            </div>
          )}
          
          {/* App description */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '15px' }}>About</h3>
            <p style={{ 
              lineHeight: '1.6',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0
            }}>
              {selectedApp.longDescription || selectedApp.description}
            </p>
          </div>
          
          {/* Screenshots */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '15px' }}>Screenshots</h3>
            <div style={{ 
              display: 'flex',
              gap: '15px',
              overflowX: 'auto',
              padding: '5px 0'
            }}>
              {selectedApp.screenshots && selectedApp.screenshots.map((screenshot, index) => (
                <img
                  key={index}
                  src={screenshot}
                  alt={`${selectedApp.name} screenshot ${index + 1}`}
                  style={{
                    height: '200px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    objectFit: 'cover'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Render content based on current navigation
  const renderContent = () => {
    // If search is active, show search results
    if (isSearchActive) {
      return renderSearchResults();
    }
    
    // If app details are open, show that instead
    if (selectedApp) {
      return renderAppDetails();
    }
    
    // Show category view
    if (selectedNav === 'category' && selectedCategory) {
      const categoryApps = getAppsByCategory(selectedCategory);
      const category = categories.find(c => c.id === selectedCategory);
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            {category ? category.title : 'Category'} Apps
          </h2>
          
          {renderAppCollection('All Apps', categoryApps)}
        </div>
      );
    }
    
    // Show apps view
    if (selectedNav === 'apps') {
      const allApps = getAppsWithStatus();
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            All Apps
          </h2>
          
          {renderAppCollection('Applications', allApps, 'Browse all applications available in the store')}
        </div>
      );
    }
    
    // Show games view
    if (selectedNav === 'games') {
      const gameApps = getAppsByCategory('games');
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            Games
          </h2>
          
          {gameApps.length > 0 ? (
            renderAppCollection('All Games', gameApps, 'Enjoy these entertaining games')
          ) : (
            <div style={{
              backgroundColor: 'rgba(30, 30, 50, 0.4)',
              borderRadius: '15px',
              padding: '40px 30px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(70, 130, 220, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="fas fa-gamepad" style={{ fontSize: '32px', opacity: 0.7 }}></i>
              </div>
              <div>
                <h3 style={{ marginBottom: '10px', fontWeight: '500' }}>No games available yet</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                  Games are coming soon! Check back later for exciting new titles.
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    // Show productivity view
    if (selectedNav === 'productivity') {
      const productivityApps = getAppsByCategory('productivity');
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            Productivity Apps
          </h2>
          
          {renderAppCollection('All Productivity Apps', productivityApps, 'Boost your productivity with these tools')}
        </div>
      );
    }
    
    // Show tools view
    if (selectedNav === 'tools') {
      const toolsApps = getAppsByCategory('tools');
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            Tools
          </h2>
          
          {renderAppCollection('All Tools', toolsApps, 'Useful utilities and tools for your system')}
        </div>
      );
    }
    
    // Show library view (installed apps)
    if (selectedNav === 'library') {
      const installedAppsList = getAppsWithStatus().filter(app => app.installed);
      
      return (
        <div style={{ padding: '25px' }}>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            marginBottom: '20px' 
          }}>
            My Library
          </h2>
          
          {installedAppsList.length > 0 ? (
            renderAppCollection('Installed Apps', installedAppsList, 'Apps you have installed on your device')
          ) : (
            <div style={{
              backgroundColor: 'rgba(30, 30, 50, 0.4)',
              borderRadius: '15px',
              padding: '40px 30px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: 'rgba(70, 130, 220, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="fas fa-download" style={{ fontSize: '32px', opacity: 0.7 }}></i>
              </div>
              <div>
                <h3 style={{ marginBottom: '10px', fontWeight: '500' }}>No apps installed yet</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                  Explore the Store to find apps and add them to your collection.
                </p>
              </div>
              <button
                onClick={() => setSelectedNav('home')}
                style={{
                  backgroundColor: 'rgba(70, 130, 220, 0.8)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '10px 25px',
                  fontSize: '15px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <i className="fas fa-store"></i>
                Browse Store
              </button>
            </div>
          )}
        </div>
      );
    }
    
    // Show home view by default
    return (
      <>
        {/* Hero Carousel */}
        <div style={{ 
          position: 'relative',
          height: '280px',
          borderRadius: '16px',
          overflow: 'hidden',
          marginBottom: '40px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          {featuredContent.map((content, index) => (
            <div 
              key={content.id}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.7), transparent), url(${content.backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                padding: '0 50px',
                opacity: currentSlide === index ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
                zIndex: currentSlide === index ? 1 : 0
              }}
            >
              <div style={{ maxWidth: '500px' }}>
                <h2 style={{ 
                  fontSize: '32px', 
                  fontWeight: '700',
                  marginBottom: '10px',
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
                }}>
                  {content.title}
                </h2>
                <p style={{ 
                  fontSize: '18px',
                  marginBottom: '20px',
                  opacity: 0.9,
                  textShadow: '0 2px 5px rgba(0, 0, 0, 0.3)'
                }}>
                  {content.subtitle}
                </p>
                {content.appId && (
                  <button 
                    onClick={() => {
                      const app = apps.find(a => a.id === content.appId);
                      if (app) openAppDetails(app);
                    }}
                    style={{
                      backgroundColor: content.color,
                      color: 'white',
                      border: 'none',
                      padding: '10px 24px',
                      borderRadius: '20px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                      transition: 'transform 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    Learn More
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Carousel navigation dots */}
          <div style={{ 
            position: 'absolute', 
            bottom: '20px', 
            left: '50%', 
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '10px',
            zIndex: 2
          }}>
            {featuredContent.map((_, index) => (
              <button 
                key={index}
                onClick={() => setCurrentSlide(index)}
                style={{
                  width: index === currentSlide ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  border: 'none',
                  padding: 0,
                  transition: 'width 0.3s, background-color 0.3s',
                  cursor: 'pointer'
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
          
        {/* Category section */}
        {renderCategorySection()}
          
        {/* Featured, top categories and other app collections */}
        <div className="app-collections-container">
          {/* Featured Apps */}
          {renderAppCollection('Featured Apps', getAppsWithStatus().filter(app => app.featured))}
          
          {/* Top categories */}
          {renderAppCollection('Productivity', getAppsByCategory('productivity'))}
          {renderAppCollection('Games', getAppsByCategory('games'))}
          {renderAppCollection('Tools', getAppsByCategory('tools'))}
          
          {/* Custom Apps */}
          {customApps.length > 0 && renderAppCollection('User-Created Apps', customApps.map(app => ({
            ...app,
            installed: isAppInstalled(app.id)
          })), 'Apps created by WebOS users')}
        </div>
      </>
    );
  };
  
  // Category section component
  const renderCategorySection = () => (
    <div style={{ marginBottom: '40px' }}>
      <h2 style={{ 
        fontSize: '20px', 
        fontWeight: '600', 
        marginBottom: '20px' 
      }}>
        Top Categories
      </h2>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
        gap: '15px' 
      }}>
        {categories.map(category => (
          <div 
            key={category.id} 
            onClick={() => {
              setSelectedNav('category');
              setSelectedCategory(category.id);
            }}
            style={{
              backgroundColor: 'rgba(30, 30, 50, 0.4)',
              backdropFilter: 'blur(10px)',
              borderRadius: '10px',
              padding: '15px',
              cursor: 'pointer',
              transition: 'transform 0.2s, background-color 0.2s',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = 'rgba(40, 40, 70, 0.5)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = 'rgba(30, 30, 50, 0.4)';
            }}
          >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={`fas fa-${category.icon}`} style={{ fontSize: '20px' }}></i>
            </div>
            <div style={{ fontWeight: '500' }}>{category.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
  
  return (
    <div style={{ 
      height: '100%',
      display: 'flex',
      overflow: 'hidden',
      backgroundColor: 'rgba(15, 15, 25, 0.3)',
      backdropFilter: 'blur(30px)',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      {/* Sidebar Navigation */}
      <div style={{ 
        width: '240px',
        backgroundColor: 'rgba(20, 20, 35, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px 0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          padding: '0 20px', 
          marginBottom: '30px', 
          display: 'flex', 
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            backgroundColor: 'rgba(80, 120, 220, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className="fas fa-store" style={{ fontSize: '20px' }}></i>
          </div>
          <h1 style={{ 
            fontSize: '20px', 
            fontWeight: '600',
            margin: 0
          }}>
            Store
          </h1>
        </div>
        
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {navItems.map(item => (
            <div 
              key={item.id}
              onClick={() => handleNavigation(item.id)}
              style={{
                padding: '12px 20px',
                marginBottom: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                backgroundColor: selectedNav === item.id ? 'rgba(70, 130, 220, 0.15)' : 'transparent',
                borderLeft: selectedNav === item.id ? '3px solid rgba(70, 130, 220, 0.8)' : '3px solid transparent',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => {
                if (selectedNav !== item.id) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                }
              }}
              onMouseOut={(e) => {
                if (selectedNav !== item.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <i className={`fas fa-${item.icon}`} style={{ 
                width: '20px', 
                fontSize: '16px',
                color: selectedNav === item.id ? 'rgba(70, 130, 220, 0.9)' : 'rgba(255, 255, 255, 0.7)'
              }}></i>
              <span style={{ 
                fontWeight: selectedNav === item.id ? '500' : 'normal',
                color: selectedNav === item.id ? 'white' : 'rgba(255, 255, 255, 0.7)'
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
        
        <div style={{ padding: '20px' }}>
          <div style={{
            padding: '12px',
            backgroundColor: 'rgba(60, 60, 80, 0.3)',
            borderRadius: '10px',
            fontSize: '14px',
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <i className="fas fa-user-circle" style={{ fontSize: '18px', marginRight: '8px' }}></i>
            User Account
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Header with search */}
        <div style={{ 
          padding: '15px 25px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            margin: 0 
          }}>
            {isSearchActive ? 'Search Results' :
             selectedApp ? 'App Details' : 
             selectedNav === 'category' ? (categories.find(c => c.id === selectedCategory)?.title || 'Category') :
             navItems.find(item => item.id === selectedNav)?.label || 'Store'}
          </h2>
          
          <div style={{ 
            width: '300px',
            backgroundColor: isSearchFocused ? 'rgba(255, 255, 255, 0.15)' : 'rgba(30, 30, 50, 0.5)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 0.2s, box-shadow 0.2s',
            boxShadow: isSearchFocused ? '0 0 0 1px rgba(70, 130, 220, 0.8)' : 'none',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}>
            <i className="fas fa-search" style={{ 
              margin: '0 12px',
              color: 'rgba(255, 255, 255, 0.6)'
            }}></i>
            <input 
              type="text"
              placeholder="Search apps, games, etc."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'white',
                padding: '10px 0',
                flex: 1,
                fontSize: '14px'
              }}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  padding: '0 12px'
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        
        {/* Scrollable content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: selectedApp ? '0' : '25px'
        }}>
          {renderContent()}
          
          {/* Bottom space for scroll */}
          <div style={{ height: '30px' }}></div>
        </div>
      </div>
    </div>
  );
};

export default Store; 